import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { OneTimePaymentActions, RecurringPaymentActions } from './actions';
import {
  selectOTActive,
  selectOTClientSecret,
  selectOTContainerId,
  selectOTElementStatus,
  selectOTIsReady,
  selectOneTime,
  selectRActive,
  selectRClientSecret,
  selectRContainerId,
  selectRElementStatus,
  selectRIsReady,
  selectRecurring,
} from './selectors';
import { StripeElementsDomAdapterService } from '../../services/stripe-elements-dom-adapter.service';
import {
  catchError,
  concatMap,
  filter,
  from,
  map,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import {
  DonationFlowHelpersActions,
  DonationFlowUiActions,
} from '../donation-flow';
import {
  ConfirmPaymentOk,
  ConfirmSetupOk,
  isOkSetupActionOneTime,
} from '../../types/payment-element/models';
import { DonationType } from '../../types/donation-flow.models';
import { SetupIntentResponse } from '../../types/stripe-models';

// - helpers de tipos
type OneTimeConfirmSucceeded = ReturnType<
  typeof OneTimePaymentActions.confirmSucceeded
>;
type RecurringConfirmSucceeded = ReturnType<
  typeof RecurringPaymentActions.confirmSucceeded
>;

function isOkPaymentAction(
  a: OneTimeConfirmSucceeded
): a is OneTimeConfirmSucceeded & { result: ConfirmPaymentOk } {
  return a.result.ok === true && a.result.type === 'payment';
}
function isOkSetupAction(
  a: RecurringConfirmSucceeded
): a is RecurringConfirmSucceeded & { result: ConfirmSetupOk } {
  return a.result.ok === true && a.result.type === 'setup';
}

@Injectable()
export class PaymentElementEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private facade: StripeElementsDomAdapterService
  ) {}

  /** ===== ONE-TIME -> donationFlow hydrate bridge ===== */
  oneTime_bridgeHydrate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneTimePaymentActions.confirmSucceeded),
      // Importante: para one-time tambien confirmas SetupIntent
      filter(isOkSetupActionOneTime),
      withLatestFrom(this.store.select(selectOTClientSecret)),
      concatMap(([a, cs]) => {
        const setupIntent: SetupIntentResponse = {
          id: a.result.intentId,
          object: 'setup_intent',
          client_secret: cs ?? undefined,
          status: a.result.status,
        };

        const actions = [
          DonationFlowUiActions.hydrateSetupIntentAfterClientConfirm({
            setupIntent,
          }),
          ...(a.result.paymentMethodId
            ? [
                DonationFlowHelpersActions.setPaymentMethodIdFromConfirm({
                  paymentMethodId: a.result.paymentMethodId,
                  setupIntentId: a.result.intentId,
                  status: a.result.status,
                }),
              ]
            : []),
        ];

        return of(...actions);
      })
    )
  );

  /** ===== RECURRING -> donationFlow hydrate bridge ===== */
  recurring_bridgeHydrate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecurringPaymentActions.confirmSucceeded),
      filter(isOkSetupAction),
      withLatestFrom(this.store.select(selectRClientSecret)),
      concatMap(([a, cs]) => {
        const setupIntent: SetupIntentResponse = {
          id: a.result.intentId,
          object: 'setup_intent',
          client_secret: cs ?? undefined,
          status: a.result.status,
        };

        const actions = [
          DonationFlowUiActions.hydrateSetupIntentAfterClientConfirm({
            setupIntent,
          }),
          ...(a.result.paymentMethodId
            ? [
                DonationFlowHelpersActions.setPaymentMethodIdFromConfirm({
                  paymentMethodId: a.result.paymentMethodId,
                  setupIntentId: a.result.intentId,
                  status: a.result.status,
                }),
              ]
            : []),
        ];

        return of(...actions);
      })
    )
  );

  /** ========================= ONE-TIME ========================= */

  oneTime_mountOnChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        OneTimePaymentActions.viewEntered,
        OneTimePaymentActions.setClientSecret,
        OneTimePaymentActions.setActive
      ),
      withLatestFrom(
        this.store.select(selectOTContainerId),
        this.store.select(selectOTClientSecret),
        this.store.select(selectOTActive),
        this.store.select(selectOTElementStatus),
        this.store.select(selectOneTime)
      ),
      filter(
        ([_, containerId, clientSecret, active]) =>
          !!containerId && !!clientSecret && !!active
      ),
      filter(
        ([_, __, clientSecret, ___, ____, state]) =>
          !(
            state.elementStatus === 'mounted' &&
            state.mountedFor === clientSecret
          )
      ),
      switchMap(([_, containerId, clientSecret]) => {
        this.store.dispatch(OneTimePaymentActions.mountRequested());
        return from(
          this.facade.mountBySelector('#' + containerId!, clientSecret!)
        ).pipe(
          map(() =>
            OneTimePaymentActions.mounted({ mountedFor: clientSecret! })
          ),
          catchError((err: any) =>
            of(
              OneTimePaymentActions.mountFailed({
                message: err?.message ?? 'Mount failed',
              })
            )
          )
        );
      })
    )
  );

  oneTime_unmountOnExit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        OneTimePaymentActions.viewExited,
        OneTimePaymentActions.setActive,
        OneTimePaymentActions.setClientSecret
      ),
      withLatestFrom(this.store.select(selectOneTime)),
      filter(
        ([act]) =>
          act.type === OneTimePaymentActions.viewExited.type ||
          (act.type === OneTimePaymentActions.setActive.type &&
            !(act as any).active) ||
          (act.type === OneTimePaymentActions.setClientSecret.type &&
            !(act as any).clientSecret)
      ),
      switchMap(() =>
        from(
          (() => {
            this.facade.unmount();
            return Promise.resolve();
          })()
        ).pipe(
          map(() => OneTimePaymentActions.unmounted()),
          catchError(() => of(OneTimePaymentActions.unmounted()))
        )
      )
    )
  );

  oneTime_confirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneTimePaymentActions.confirmRequested),
      withLatestFrom(
        this.store.select(selectOTIsReady),
        this.store.select(selectOTActive)
      ),
      switchMap(([_, isReady, active]) => {
        if (!isReady || !active) {
          return of(
            OneTimePaymentActions.confirmFailed({
              error: 'Payment form is not ready yet.',
            })
          );
        }

        return from(this.facade.confirmSetup()).pipe(
          map((res) =>
            res.ok
              ? OneTimePaymentActions.confirmSucceeded({ result: res })
              : OneTimePaymentActions.confirmFailed({ error: res })
          ),
          catchError((err: any) =>
            of(
              OneTimePaymentActions.confirmFailed({
                error: err ?? 'Confirm failed',
              })
            )
          )
        );
      })
    )
  );

  /** ========================= RECURRING ========================= */

  recurring_mountOnChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        RecurringPaymentActions.viewEntered,
        RecurringPaymentActions.setClientSecret,
        RecurringPaymentActions.setActive
      ),
      withLatestFrom(
        this.store.select(selectRContainerId),
        this.store.select(selectRClientSecret),
        this.store.select(selectRActive),
        this.store.select(selectRElementStatus),
        this.store.select(selectRecurring)
      ),
      filter(
        ([_, containerId, clientSecret, active]) =>
          !!containerId && !!clientSecret && !!active
      ),
      filter(
        ([_, __, clientSecret, ___, ____, state]) =>
          !(
            state.elementStatus === 'mounted' &&
            state.mountedFor === clientSecret
          )
      ),
      switchMap(([_, containerId, clientSecret]) => {
        this.store.dispatch(RecurringPaymentActions.mountRequested());
        return from(
          this.facade.mountBySelector('#' + containerId!, clientSecret!)
        ).pipe(
          map(() =>
            RecurringPaymentActions.mounted({ mountedFor: clientSecret! })
          ),
          catchError((err: any) =>
            of(
              RecurringPaymentActions.mountFailed({
                message: err?.message ?? 'Mount failed',
              })
            )
          )
        );
      })
    )
  );

  recurring_unmountOnExit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        RecurringPaymentActions.viewExited,
        RecurringPaymentActions.setActive,
        RecurringPaymentActions.setClientSecret
      ),
      withLatestFrom(this.store.select(selectRecurring)),
      filter(
        ([act]) =>
          act.type === RecurringPaymentActions.viewExited.type ||
          (act.type === RecurringPaymentActions.setActive.type &&
            !(act as any).active) ||
          (act.type === RecurringPaymentActions.setClientSecret.type &&
            !(act as any).clientSecret)
      ),
      switchMap(() =>
        from(
          (() => {
            this.facade.unmount();
            return Promise.resolve();
          })()
        ).pipe(
          map(() => RecurringPaymentActions.unmounted()),
          catchError(() => of(RecurringPaymentActions.unmounted()))
        )
      )
    )
  );

  recurring_confirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecurringPaymentActions.confirmRequested),
      withLatestFrom(
        this.store.select(selectRIsReady),
        this.store.select(selectRActive)
      ),
      switchMap(([_, isReady, active]) => {
        if (!isReady || !active) {
          return of(
            RecurringPaymentActions.confirmFailed({
              error: 'Payment form is not ready yet.',
            })
          );
        }

        return from(this.facade.confirmSetup()).pipe(
          map((res) =>
            res.ok
              ? RecurringPaymentActions.confirmSucceeded({ result: res })
              : RecurringPaymentActions.confirmFailed({ error: res })
          ),
          catchError((err: any) =>
            of(
              RecurringPaymentActions.confirmFailed({
                error: err ?? 'Confirm failed',
              })
            )
          )
        );
      })
    )
  );

  /**
   * When the donor switches OneTime ↔ Recurring in step 1 we tear down the
   * branch being left behind. Resetting the destination branch too can race
   * with the new SetupIntent hydration and leave it stuck in skeleton state.
   */
  resetBothBranchesOnDonationTypeChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DonationFlowUiActions.changeDonationType),
      switchMap(({ donationType }) =>
        donationType === DonationType.Recurring
          ? of(
              OneTimePaymentActions.viewExited(),
              OneTimePaymentActions.setClientSecret({ clientSecret: null }),
              OneTimePaymentActions.setActive({ active: false })
            )
          : of(
              RecurringPaymentActions.viewExited(),
              RecurringPaymentActions.setClientSecret({ clientSecret: null }),
              RecurringPaymentActions.setActive({ active: false })
            )
      )
    )
  );
}
